import { clone } from '@jkhy/frontend-utils/functions';
import { CompanyAccountModelDto as BoCompanyAccountModelDto } from '@treasury/api/bo';
import { CompanyAccountModelDto as ConsumerCompanyAccountModelDto } from '@treasury/api/channel';
import { exists } from '@treasury/utils';
import { DomainEntity } from '../shared';

export const emptyAdditionalAccountDetails = {
    originalLoanAmount: 0,
    originalBalance: 0,
    maturityDate: '',
    interestRate: 0,
    currentPayoff: 0,
    payoffGoodThru: '',
    interestPaidYtd: 0,
    paymentDueDate: '',
    paymentAmountDue: 0,
    pastAmountDue: 0,
    totalAmountDue: 0,
    billedPrincipal: 0,
    billedInterest: 0,
    billedEscrow: 0,
    billedLateCharge: 0,
    billedOtherCharge: 0,
    paymentTotalAmount: 0,
    remainingPayments: 0,
} as const;

export const emptyCompanyAccount: ConsumerCompanyAccountModelDto = {
    id: 0,
    updatedDate: new Date().toDateString(),
    name: '',
    number: '',
    status: '',
    account: {
        id: 0,
        number: '',
        type: '',
        coreStatus: '',
        accountDisplayLabel: '',
        name: '',
        accountUniqueId: '',
    },
    currentBalance: 0,
    collectedBalance: 0,
    availableBalance: 0,
    type: '',
    perTransactionLimit: 0,
    dailyLimit: 0,
    cifNumber: '',
    isDeleted: false,
    showAccountNickName: false,
    accountDisplayLabel: '',
    transferAmount: 0,
    transferMemo: '',
    loanPaymentAmountDue: 0,
    loanPaymentDueDate: new Date().toDateString(),
    allowLoanPaymentPastDue: false,
    accountUniqueId: '',
    ...emptyAdditionalAccountDetails,
};

export class CompanyAccount extends DomainEntity<
    BoCompanyAccountModelDto | ConsumerCompanyAccountModelDto
> {
    constructor(
        dto: BoCompanyAccountModelDto | ConsumerCompanyAccountModelDto = clone(emptyCompanyAccount)
    ) {
        super(dto);
    }

    public createDefault(): this {
        return new CompanyAccount(clone(emptyCompanyAccount)) as this;
    }

    /**
     * Determines if the supplied DTO is using the Consumer shape
     */
    public isConsumerDto(): this is this & { dto: ConsumerCompanyAccountModelDto } {
        const consumerDto = this.dto as ConsumerCompanyAccountModelDto;
        return (
            exists(consumerDto) &&
            'allowLoanPaymentPastDue' in consumerDto &&
            'showAccountNickName' in consumerDto &&
            'loanPaymentAmountDue' in consumerDto &&
            'loanPaymentDueDate' in consumerDto
        );
    }

    public get account() {
        return this.isConsumerDto() ? this.dto.account?.accountDisplayLabel : undefined;
    }

    public get id() {
        return this.dto.id;
    }

    public get name() {
        return this.dto.name;
    }

    public get accountId() {
        return this.dto.account?.id;
    }

    public get accountNumber() {
        return this.dto.number;
    }

    public get cifNumber() {
        return this.dto.cifNumber || '';
    }

    public get accountName() {
        return this.dto.name;
    }

    public get number() {
        return this.dto.number;
    }

    public get status() {
        return this.dto.status;
    }

    public get type() {
        return this.dto.type;
    }

    public get currentBalance() {
        return this.isConsumerDto() ? this.dto.currentBalance : undefined;
    }

    public get collectedBalance() {
        return this.isConsumerDto() ? this.dto.collectedBalance : undefined;
    }

    public get availableBalance() {
        return this.isConsumerDto() ? this.dto.availableBalance : undefined;
    }

    public get perTransactionLimit() {
        return this.dto.perTransactionLimit;
    }

    public get dailyLimit() {
        return this.dto.dailyLimit;
    }

    public get showAccountNickName() {
        return this.isConsumerDto() ? this.dto.showAccountNickName : undefined;
    }

    public get accountDisplayLabel() {
        return this.isConsumerDto() ? this.dto.accountDisplayLabel : undefined;
    }

    public get accountUniqueId() {
        return this.dto.accountUniqueId;
    }

    public get loanPaymentAmountDue() {
        return this.isConsumerDto() ? this.dto.loanPaymentAmountDue : undefined;
    }

    public get loanPaymentDueDate() {
        return this.isConsumerDto() ? this.dto.loanPaymentDueDate : undefined;
    }
}
